/**
 * Auth state — current user from /auth/me, with login/logout helpers.
 * Lot 1c (2026-10-28) : sessions de 7 jours renouvelées en silence (POST /auth/refresh dès que le
 * jeton a plus de 24 h) ; une erreur passagère de /auth/me ne déconnecte plus — seul un 401 le fait.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest, getToken, setToken, clearToken } from '@/lib/api';
import { identify, reset as resetAnalytics, track } from '@/lib/posthog';
import i18n, { SUPPORTED_LANGS, ensureLangLoaded, type LangCode } from '@/i18n';

export interface CurrentUser {
  id: string;
  email: string;
  display_name: string | null;
  plan: string | null;
  full_name: string | null;
  onboarding_completed: boolean | null;
  preferred_lang?: string | null;
  /** Lot 1d — univers de l'espace : `mars` (partenaires) ou `mr_crm` (équipe M&R) ; décide l'espace par défaut. */
  origin?: 'mars' | 'mr_crm' | null;
}

/**
 * Aligne l'interface sur la langue du compte. Le SSO Mars le faisait déjà ;
 * la connexion par mot de passe laissait décider le navigateur — un membre
 * francophone pouvait donc atterrir en anglais.
 */
async function applyAccountLang(lang: string | null | undefined): Promise<void> {
  if (!lang || !SUPPORTED_LANGS.some((l) => l.code === lang)) return;
  try {
    localStorage.setItem('nia-lang', lang);
  } catch {
    /* stockage indisponible : la langue s'appliquera quand même pour cette session */
  }
  await ensureLangLoaded(lang as LangCode);
  await i18n.changeLanguage(lang);
}

const REFRESH_AFTER_SECONDS = 24 * 3600;
let refreshing: Promise<void> | null = null;

/** Âge du jeton en secondes d'après son `iat`, sans vérifier la signature (le serveur s'en charge). */
export function tokenAgeSeconds(token: string): number | null {
  try {
    const part = token.split('.')[1] ?? '';
    const padded = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(part.length + ((4 - (part.length % 4)) % 4), '=');
    const iat = (JSON.parse(atob(padded)) as { iat?: unknown }).iat;
    return typeof iat === 'number' ? Math.max(0, Math.floor(Date.now() / 1000) - iat) : null;
  } catch {
    return null;
  }
}

/** Renouvelle le jeton en silence s'il a plus de 24 h ; jamais d'erreur visible, jamais deux appels en même temps. */
export function maybeRefreshToken(): Promise<void> {
  const token = getToken();
  if (!token) return Promise.resolve();
  const age = tokenAgeSeconds(token);
  if (age === null || age < REFRESH_AFTER_SECONDS) return Promise.resolve();
  if (!refreshing) {
    refreshing = apiRequest<{ token: string }>('/auth/refresh', { method: 'POST' })
      .then((r) => { if (r.token && getToken() === token) setToken(r.token); })
      .catch(() => undefined)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export function useAuth() {
  const qc = useQueryClient();

  const query = useQuery<CurrentUser | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      if (!getToken()) return null;
      try {
        const me = await apiRequest<CurrentUser>('/auth/me');
        void maybeRefreshToken();
        return me;
      } catch (e) {
        // Seul un jeton refusé déconnecte ; une panne passagère (réseau, 5xx) garde la session (lot 1c).
        if (e instanceof ApiError && e.status === 401) {
          clearToken();
          return null;
        }
        throw e;
      }
    },
    retry: (count, e) => !(e instanceof ApiError && e.status === 401) && count < 2,
    // Panne qui dure : on réessaie toutes les 5 s tant qu'un jeton existe (l'app montre le chargeur, pas la connexion).
    refetchInterval: (q) => (q.state.status === 'error' && !!getToken() ? 5000 : false),
    staleTime: Infinity,
  });

  async function login(email: string, password: string): Promise<void> {
    const res = await apiRequest<{ token: string; user?: { id: string; preferred_lang?: string | null } }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    });
    setToken(res.token);
    if (res.user?.id) identify(res.user.id, { email });
    track('user_login', { method: 'password' });
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    const me = qc.getQueryData<CurrentUser | null>(['auth', 'me'])
      ?? (await qc.fetchQuery<CurrentUser | null>({ queryKey: ['auth', 'me'] }));
    await applyAccountLang(me?.preferred_lang);
  }

  async function register(
    email: string,
    password: string,
    displayName?: string,
    preferredLang?: string,
    viaInvite?: boolean,
  ): Promise<void> {
    const res = await apiRequest<{ token: string; user?: { id: string } }>('/auth/register', {
      method: 'POST',
      body: { email, password, displayName, preferredLang, viaInvite },
      auth: false,
    });
    setToken(res.token);
    if (res.user?.id) identify(res.user.id, { email, preferred_lang: preferredLang });
    track('user_signup', { lang: preferredLang ?? 'fr', via_invite: viaInvite === true });
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
  }

  function logout(): void {
    track('user_logout');
    resetAnalytics();
    // Lot 1c : révoque le jeton côté serveur (plus de renouvellement possible), sans attendre la réponse.
    if (getToken()) void apiRequest<void>('/auth/logout', { method: 'POST' }).catch(() => undefined);
    clearToken();
    qc.setQueryData(['auth', 'me'], null);
  }

  /**
   * Relit /auth/me depuis le serveur (17/09/2026).
   *
   * La requête ['auth','me'] a `staleTime: Infinity` : une fois chargée, elle ne
   * se rafraîchit JAMAIS pendant la session. Quand l'entretien avec Inea se
   * termine, le serveur passe onboarding_completed à TRUE mais le cache du
   * navigateur reste à FALSE — et RequireOnboarding renvoie l'utilisateur sur
   * /onboarding en boucle, sans issue. À appeler avant toute navigation qui
   * dépend de onboarding_completed.
   */
  async function refresh(): Promise<CurrentUser | null> {
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    return (await qc.fetchQuery<CurrentUser | null>({ queryKey: ['auth', 'me'] })) ?? null;
  }

  return {
    user: query.data ?? null,
    // Lot 1c : une panne passagère avec un jeton en place reste un « chargement » (jamais un retour à la connexion).
    isLoading: query.isLoading || (query.isError && !!getToken()),
    isAuthenticated: !!query.data,
    login,
    register,
    logout,
    refresh,
  };
}
